import { Button } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import EntrantColumn from "~pages/Officials/AssignFixtureOfficialsPage/components/EntrantColumn";
import type { Image } from "~types/image";
import type { AssignFixtureOfficialsTableRow } from "../types/assignFixtureOfficialsTableRow";

export const useAssignFixtureOfficialsColumns = (setFixtureId: (fixtureId: string) => void) => {
  const { formatMessage, formatDate } = useIntl();
  const { competitorType } = useSportConfiguration();

  const { hasPermission } = useUserPermissions();
  const readRolesAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.READ);

  const columns: ColumnType<AssignFixtureOfficialsTableRow>[] = [
    {
      title: formatMessage({ id: "round" }),
      dataIndex: "roundNumber",
      width: 50,
      align: "center",
    },
    {
      title: competitorType === CompetitorTypes.Entity ? formatMessage({ id: "team.home" }) : formatMessage({ id: "persons.home" }),
      dataIndex: "homeEntrants",
      align: "right",
      render: (value: { nameFullLocal: string; images: Image[]; id: string }[]) =>
        value?.map((entrant) => (
          <div key={entrant.id}>
            <EntrantColumn entrant={entrant} isHome />
          </div>
        )),
    },
    {
      title: competitorType === CompetitorTypes.Entity ? formatMessage({ id: "team.away" }) : formatMessage({ id: "persons.away" }),
      dataIndex: "awayEntrants",
      align: "left",
      render: (value: { nameFullLocal: string; images: Image[]; id: string }[]) =>
        value?.map((entrant) => (
          <div key={entrant.id}>
            <EntrantColumn entrant={entrant} />
          </div>
        )),
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.fixtureNumber",
      }),
      dataIndex: "fixtureNumber",
      width: 90,
      sorter: true,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
    },
    {
      title: formatMessage({ id: "venue" }),
      dataIndex: "venue",
      width: 200,
    },
    {
      title: formatMessage({ id: "date.start" }),
      dataIndex: "startTimeLocal",
      width: 85,
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      align: "center",
      render: (value: string) => formatDate(value),
    },
    {
      title: formatMessage({ id: "date.startTime" }),
      dataIndex: "startTimeLocal",
      width: 100,
      align: "center",
      render: (value: string) => formatDate(value, { hour: "numeric", minute: "numeric" }),
    },
    {
      title: formatMessage({ id: "type" }),
      dataIndex: "fixtureType",
      width: 85,
      align: "center",
      render: (value: string) => (value ? formatMessage({ id: `fixtureType.${value}` }) : ""),
    },
    {
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      width: 90,
      align: "center",
      render: (value: string) => (value ? formatMessage({ id: `fixtureStatus.${value}` }) : ""),
    },
    {
      title: formatMessage({ id: "stage" }),
      dataIndex: "stage",
      width: 200,
    },
    {
      title: formatMessage({ id: "pool" }),
      dataIndex: "pool",
      width: 200,
    },
    {
      title: formatMessage({ id: "series" }),
      dataIndex: "series",
      width: 200,
    },
    {
      width: 80,
      align: "right",
      render: (_value, row) =>
        readRolesAllowed ? (
          <Button type="primary" size="small" onClick={() => setFixtureId(row.fixtureId)}>
            {formatMessage({ id: "officials.assign" })}
          </Button>
        ) : null,
    },
  ];

  return columns;
};
